import React, { SyntheticEvent, useEffect, useState } from 'react'
import { flowRight as compose } from 'lodash'
import { RouteChildrenProps, useHistory } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import {
  StockKeepingUnitList,
  StockKeepingUnitSearchAndFilter,
  StockKeepingUnitService,
  getQueryParams,
  HEADERS,
  HEADERS_LOADING,
  BREADCRUMBS_LIST,
  SKUTEMPLATE,
  StockKeepingUnitDownloadReport,
} from 'features/StockKeepingUnit'
import { Button, Pagination, Table, BulletLabel } from 'components'
import { IconImport, IconPlus } from 'components/Icons'
import { THead } from 'components/Table'
import { LIMIT_PAGE } from 'helpers/constants/common'
import { withBackOfficeCms } from 'helpers/hocs'
import { useBreadcrumbs, useCancelToken, useSearchParams } from 'helpers/hooks'
import { routes } from 'helpers/routes'
import { onPageChange } from 'helpers/common'

import type { IStockKeepingUnitSingleProps } from 'features/StockKeepingUnit'
import type { IParamQuery } from 'libs/apis/helper'
import type { ISorting } from 'types'
import { OnboardingContainer } from 'containers/Onboarding'
import { downloadTemplate } from 'helpers/download'

const StockKeepingUnitContainer = ({
  location,
}: RouteChildrenProps): JSX.Element => {
  const [loading, setLoading] = useState(true)
  const [isError, setIsError] = useState<boolean>(false)
  const [totals, setTotals] = useState(1)
  const [showMenu, setShowMenu] = useState(false)
  const [stockKeepingUnits, setStockKeepingUnits] = useState<
    IStockKeepingUnitSingleProps[]
  >([])
  const [searchParams, setSearchParams] = useSearchParams<IParamQuery>({
    _limit: 30,
    _page: 1,
    isActive: true,
  })
  const history = useHistory()
  const { t } = useTranslation(['layout', 'sku', 'product', 'common'])
  const { newCancelToken, isCancel } = useCancelToken()
  const [isOpenOnboarding, setIsOpenOnboarding] = useState(false)
  const [template, setTemplateType] = useState<string>('')

  const params = getQueryParams(history.location.search)
  const base = 'con-stock-keeping-unit'
  const buttonSKU = 'con-import-sku'

  const getStockKeepingUnits = async (): Promise<void> => {
    setIsError(false)
    setLoading(true)

    try {
      const stockKeepingUnits = await StockKeepingUnitService.findAll(
        searchParams,
        {
          cancelToken: newCancelToken(),
        },
      )
      const { body, totals } = stockKeepingUnits

      setTotals(totals)
      setStockKeepingUnits(body)
      setLoading(false)
    } catch (e) {
      if (isCancel(e)) return

      setIsError(true)
      setLoading(false)
    }
  }

  const handleToggle = (): void => {
    setShowMenu((prev) => !prev)
  }

  const handleClickRow = (id: string) => (e: SyntheticEvent) => {
    e.preventDefault()

    history.push(`${routes.product.path}/${id}/detail`)
  }

  const onSortChange = (sort: ISorting): void =>
    setSearchParams({ _sort: sort.column, _order: sort.order, _page: 1 })

  useEffect(() => {
    getStockKeepingUnits()
  }, [location])

  useBreadcrumbs(BREADCRUMBS_LIST)

  const totalPages = Math.ceil(totals / LIMIT_PAGE)

  const getExcelTemplate = async () => {
    try {
      const template = await StockKeepingUnitService.downloadSkuTemplate()
      downloadTemplate(template, SKUTEMPLATE)
    } catch (e) {
      if (isCancel(e)) return
    }
  }

  return (
    <div className='container-list'>
      <StockKeepingUnitSearchAndFilter isLoading={loading} />

      <div className='float-button--wrap'>
        <div className='float-button--group'>
          <div className='float-button--container'>
            <Button
              className={`${buttonSKU}__form-btn`}
              variants='outline'
              withIcon={<IconImport variants='sm' />}
              onClick={() => {
                setTemplateType('sku')
                setIsOpenOnboarding(true)
              }}
            >
              {t('product:import.sku.button')}
            </Button>
            <Button
              className={`${base}__form-btn`}
              onClick={() => history.push(routes.product.create.path)}
              variants='primary'
              withIcon={<IconPlus variants='sm' />}
            >
              {t('product:create.button')}
            </Button>
          </div>
        </div>
      </div>

      <div className='list-content'>
        <div className='content-bullet'>
          <BulletLabel isActive isShowLabel />
          <BulletLabel isShowLabel />
        </div>
        <div className='row'>
          <Table>
            <THead
              columns={HEADERS}
              onSortChange={onSortChange}
              sorting={{
                column: searchParams._sort as string,
                order: searchParams._order || 'ASC',
              }}
              onToggleChange={handleToggle}
              position='left'
            />
            <StockKeepingUnitList
              columns={loading ? HEADERS_LOADING : HEADERS}
              stockKeepingUnits={stockKeepingUnits}
              showMenu={showMenu}
              loading={loading}
              isError={isError}
              onClickRow={handleClickRow}
            />
          </Table>
        </div>
      </div>

      <div className={`${base}__con-bottom`}>
        <StockKeepingUnitDownloadReport />
        <Pagination
          currentPage={params._page}
          totalPages={totalPages}
          onChange={(page) =>
            onPageChange(() => setSearchParams({ _page: page }))
          }
          disabled={loading}
        />
      </div>

      <OnboardingContainer
        isOpen={isOpenOnboarding}
        onClose={() => setIsOpenOnboarding(false)}
        type={template}
        onDownload={() => getExcelTemplate()}
      />
    </div>
  )
}

export default compose(withBackOfficeCms)(StockKeepingUnitContainer)
